/* eslint-disable no-console */

/**
 * Storage Manager Utility
 * Handles client-side data persistence and localStorage operations
 */

export class StorageManager {
  constructor () {
    this.prefix = 'atb_';
    this.fallbackStorage = new Map();
    this.isInitialized = false;
    this.metrics = {
      reads: 0,
      writes: 0,
      errors: 0,
      fallbackReads: 0,
      fallbackWrites: 0
    };
  }

  async init () {
    try {
      this.localStorageAvailable = this.checkLocalStorage();
      if (!this.localStorageAvailable) {
        console.warn('⚠️ [STORAGE] localStorage not available, using in-memory fallback');
      }

      await this.logStorageEstimate();
      this.isInitialized = true;
      return true;
    } catch (error) {
      this.metrics.errors++;
      console.error('❌ [STORAGE] Initialization failed:', error);
      this.isInitialized = false;
      return false;
    }
  }

  checkLocalStorage () {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      const testKey = `${this.prefix}__test__`;
      window.localStorage.setItem(testKey, 'ok');
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('⚠️ [STORAGE] Unable to access localStorage:', error.message);
      return false;
    }
  }

  async logStorageEstimate () {
    if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
      return;
    }
    try {
      const { quota = 0, usage = 0 } = await navigator.storage.estimate();
      console.log('📊 [STORAGE] Quota estimate:', {
        quotaBytes: quota,
        usageBytes: usage
      });
    } catch (error) {
      console.warn('⚠️ [STORAGE] Unable to estimate storage:', error.message);
    }
  }

  makeKey (key) {
    return `${this.prefix}${key}`;
  }

  serialize (value) {
    try {
      return JSON.stringify(value);
    } catch (error) {
      console.warn('⚠️ [STORAGE] Failed to serialize value, storing as string:', error.message);
      return String(value);
    }
  }

  deserialize (value, defaultValue) {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      return value;
    }
  }

  get (key, defaultValue = null) {
    const namespacedKey = this.makeKey(key);
    try {
      let rawValue = null;
      if (this.localStorageAvailable) {
        rawValue = window.localStorage.getItem(namespacedKey);
        this.metrics.reads++;
      } else if (this.fallbackStorage.has(namespacedKey)) {
        rawValue = this.fallbackStorage.get(namespacedKey);
        this.metrics.fallbackReads++;
      }
      return this.deserialize(rawValue, defaultValue);
    } catch (error) {
      this.metrics.errors++;
      console.error('❌ [STORAGE] Failed to read key:', key, error);
      return defaultValue;
    }
  }

  set (key, value) {
    const namespacedKey = this.makeKey(key);
    const payload = this.serialize(value);
    try {
      if (this.localStorageAvailable) {
        window.localStorage.setItem(namespacedKey, payload);
        this.metrics.writes++;
      } else {
        this.fallbackStorage.set(namespacedKey, payload);
        this.metrics.fallbackWrites++;
      }
      return true;
    } catch (error) {
      this.metrics.errors++;
      console.error('❌ [STORAGE] Failed to write key:', key, error);
      return false;
    }
  }

  remove (key) {
    const namespacedKey = this.makeKey(key);
    if (this.localStorageAvailable) {
      window.localStorage.removeItem(namespacedKey);
    }
    this.fallbackStorage.delete(namespacedKey);
  }

  clear () {
    if (this.localStorageAvailable) {
      Object.keys(window.localStorage)
        .filter(key => key.startsWith(this.prefix))
        .forEach(key => window.localStorage.removeItem(key));
    }
    this.fallbackStorage.clear();
  }

  getAllKeys () {
    const keys = new Set();
    if (this.localStorageAvailable) {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && key.startsWith(this.prefix)) {
          keys.add(key.replace(this.prefix, ''));
        }
      }
    }
    this.fallbackStorage.forEach((_, key) => {
      if (key.startsWith(this.prefix)) {
        keys.add(key.replace(this.prefix, ''));
      }
    });
    return Array.from(keys);
  }

  async getStorageStats () {
    if (typeof navigator === 'undefined' || !navigator.storage || !navigator.storage.estimate) {
      return null;
    }
    try {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota,
        usage: estimate.usage,
        percentageUsed: estimate.quota ? Math.round((estimate.usage / estimate.quota) * 100) : null
      };
    } catch (error) {
      console.warn('⚠️ [STORAGE] Unable to get stats:', error.message);
      return null;
    }
  }

  backup () {
    const data = {};
    this.getAllKeys().forEach(key => {
      data[key] = this.get(key);
    });
    return {
      timestamp: new Date().toISOString(),
      keys: this.getAllKeys(),
      data
    };
  }

  restore (backupObject) {
    if (!backupObject || typeof backupObject !== 'object') {
      return false;
    }
    const { data = {} } = backupObject;
    Object.entries(data).forEach(([key, value]) => {
      this.set(key, value);
    });
    return true;
  }
}
